// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { ISO_3166_1_ALPHA_2 } from '../constants/iso-3166-1-alpha-2.js';
/** The locales whose country names are read. The platform's sixteen. */
const NAME_LOCALES = [
    'en',
    'vi',
    'de',
    'fr',
    'nl',
    'es',
    'pt',
    'da',
    'sv',
    'fi',
    'nb',
    'nn',
    'ca',
    'eu',
    'ga',
    'lb',
];
const CODES = new Set(ISO_3166_1_ALPHA_2);
/** A name folded for comparison: no case, accents, spaces or punctuation. */
function foldName(name) {
    return name
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[đĐ]/g, 'd')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '');
}
let nameIndex = null;
function namesToCodes() {
    if (nameIndex)
        return nameIndex;
    const index = new Map();
    const ambiguous = new Set();
    for (const locale of NAME_LOCALES) {
        let display;
        try {
            display = new Intl.DisplayNames([locale], { type: 'region' });
        }
        catch {
            continue;
        }
        for (const code of ISO_3166_1_ALPHA_2) {
            let name;
            try {
                name = display.of(code);
            }
            catch {
                name = undefined;
            }
            if (!name || name === code)
                continue;
            const key = foldName(name);
            if (key === '' || ambiguous.has(key))
                continue;
            const seen = index.get(key);
            if (seen === undefined)
                index.set(key, code);
            else if (seen !== code) {
                index.delete(key);
                ambiguous.add(key);
            }
        }
    }
    nameIndex = index;
    return index;
}
export function resolveCountryCode(value) {
    if (value === null || value === undefined)
        return { status: 'absent' };
    const input = typeof value === 'string' ? value : String(value);
    const trimmed = input.trim();
    if (trimmed === '')
        return { status: 'absent' };
    const upper = trimmed.toUpperCase();
    if (CODES.has(upper))
        return { status: 'resolved', code: upper, input };
    const byName = namesToCodes().get(foldName(trimmed));
    if (byName)
        return { status: 'resolved', code: byName, input };
    return { status: 'unrecognised', input };
}
/**
 * Sprint 63.37.0 (legal second read S12) — a recorded country, written as the
 * country's name in `locale`.
 *
 * The prefill engine printed a transfer's destination as its ISO code (`SG`)
 * on a Vietnamese administrative filing whose field asks for a country
 * ("Quốc gia bên nhận"). This reads the value through
 * {@link resolveCountryCode}, so a code in any case, or a name in any of the
 * platform's locales, is the same country, and names it with the runtime's
 * own region data (`Intl.DisplayNames`), as the country pickers do. No country
 * name is a literal here (R-46).
 *
 * Returns `null` when nothing is recorded, when the value is not a country the
 * resolver recognises, or when the runtime has no name for it in `locale`
 * (`Intl.DisplayNames` answers with the code itself then): a code is never
 * returned as if it were a name.
 */
export function countryName(value, locale) {
    const resolved = resolveCountryCode(value);
    if (resolved.status !== 'resolved')
        return null;
    let name;
    try {
        name = new Intl.DisplayNames([locale], { type: 'region' }).of(resolved.code);
    }
    catch {
        name = undefined;
    }
    const trimmed = name?.trim();
    return trimmed && trimmed.toUpperCase() !== resolved.code ? trimmed : null;
}
//# sourceMappingURL=country-code.js.map